'use client'

import React, { useState, useEffect, useMemo } from 'react'
import { roboto } from '@/lib/fonts'
import '../../../sass/menu/OrderingWidget.scss'
import { useCart } from '@/contexts/CartContext'
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Grid,
  IconButton,
  TextField,
  Box
} from '@mui/material'
import { Minus, Plus } from 'lucide-react'
import { v4 as uuidv4 } from 'uuid'
import { Variant } from '@/lib/interfaces/ordering'
import { Language } from '@/lib/interfaces/interfaces'

interface Props {
  isVisible: boolean;
  itemId: string;
  itemName: string;
  halfprice?: string | number;
  price?: string | number;
  tapaprice?: string | number;
  closePopup: () => void;
  language: Language;
}

// Objeto de traducciones para cada idioma
const translations = {
  es: {
    variantLabel: 'Variante',
    priceLabel: 'Precio',
    decrementQuantity: 'decrementar cantidad',
    incrementQuantity: 'incrementar cantidad',
    addToCart: 'Añadir'
  },
  en: {
    variantLabel: 'Variant',
    priceLabel: 'Price',
    decrementQuantity: 'Decrease quantity',
    incrementQuantity: 'Increase quantity',
    addToCart: 'Add to Cart'
  },
  fr: {
    variantLabel: 'Variante',
    priceLabel: 'Prix',
    decrementQuantity: 'Diminuer la quantité',
    incrementQuantity: 'Augmenter la quantité',
    addToCart: 'Ajouter'
  }
}

// Función para renderizar el indicador visual de círculos
const getIndicatorCircles = (type: Variant['type']) => {
  let count = 0
  if (type === 'full') count = 3
  else if (type === 'half') count = 2
  else if (type === 'tapa') count = 1
  return (
    <Box display='flex' alignItems='center'>
      {Array.from({ length: count }).map((_, index) => (
        <Box
          key={index}
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: '#659ad3',
            marginRight: index < count - 1 ? '2px' : 0
          }}
        />
      ))}
    </Box>
  )
}

// Función de fábrica para crear variantes
const createVariant = (type: Variant['type'], price: number): Variant => ({
  type,
  price
})

const OrderingWidget = ({
  isVisible,
  itemId,
  itemName,
  halfprice,
  price,
  tapaprice,
  closePopup,
  language
}: Props) => {
  const { addToCart } = useCart()
  const [quantity, setQuantity] = useState<number>(1)
  const [selectedVariant, setSelectedVariant] = useState<Variant['type'] | ''>('')

  // Memorizar las variantes para evitar recrearlas en cada render
  const variants: Variant[] = useMemo(() => [
    ...(price !== undefined && parseFloat(price.toString()) > 0
      ? [createVariant('full', typeof price === 'string' ? parseFloat(price) : price)]
      : []),
    ...(halfprice !== undefined && parseFloat(halfprice.toString()) > 0
      ? [createVariant('half', typeof halfprice === 'string' ? parseFloat(halfprice) : halfprice)]
      : []),
    ...(tapaprice !== undefined && parseFloat(tapaprice.toString()) > 0
      ? [createVariant('tapa', typeof tapaprice === 'string' ? parseFloat(tapaprice) : tapaprice)]
      : [])
  ], [price, halfprice, tapaprice])

  // Efecto para asignar la variante automáticamente si solo hay una disponible
  useEffect(() => {
    if (variants.length === 1) {
      setSelectedVariant(variants[0].type)
    } else if (variants.length > 1 && !variants.find(v => v.type === selectedVariant)) {
      setSelectedVariant(variants[0].type)
    }
  }, [variants, selectedVariant])

  const handleAddToCart = () => {
    const variant = variants.find((v) => v.type === selectedVariant)
    if (!variant) {
      console.error('Variante seleccionada no válida')
      return
    }

    addToCart({
      id: itemId,
      name: itemName,
      variant,
      quantity,
      cartItemId: uuidv4()
    })

    setQuantity(1)
    if (variants.length > 1) {
      setSelectedVariant(variants[0].type)
    }
    closePopup()
  }

  const handleIncrement = () => {
    setQuantity(prev => prev + 1)
  }

  const handleDecrement = () => {
    setQuantity(prev => (prev > 1 ? prev - 1 : 1))
  }

  const texts = translations[language] || translations.es

  return (
    <div
      style={roboto.style}
      className={`detailed-item ${isVisible ? 'scale-up' : 'scale-down'} ordering-widget`}
      onClick={(e) => e.stopPropagation()}
    >
      <Grid container spacing={2} alignItems='center'>
        {/* Selector de Variante o Precio */}
        {variants.length > 1 && (
          <Grid
            item
            xs={6}
            sm={6}
            sx={{
              '@media (max-width: 400px)': {
                flexBasis: '100%',
                maxWidth: '100%'
              }
            }}
          >
            <FormControl fullWidth size='small'>
              <InputLabel id={`variant-label-${itemId}`}>{texts.variantLabel}</InputLabel>
              <Select
                labelId={`variant-label-${itemId}`}
                id={`variant-select-${itemId}`}
                value={selectedVariant}
                label={texts.variantLabel}
                onChange={(e) => setSelectedVariant(e.target.value as Variant['type'])}
              >
                {variants.map((variant) => (
                  <MenuItem key={variant.type} value={variant.type}>
                    <Box display='flex' alignItems='center' gap={1}>
                      {getIndicatorCircles(variant.type)}
                      <span>{variant.price}€</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {variants.length === 1 && (
          <Grid
            item
            xs={6}
            sm={6}
            sx={{
              '@media (max-width: 400px)': {
                flexBasis: '100%',
                maxWidth: '100%'
              }
            }}
          >
            <TextField
              fullWidth
              size='small'
              label={texts.priceLabel}
              value={`${variants[0].price}€`}
              InputProps={{
                readOnly: true
              }}
            />
          </Grid>
        )}

        {/* Selector de Cantidad */}
        <Grid
          item
          xs={6}
          sm={6}
          sx={{
            '@media (max-width: 400px)': {
              flexBasis: '100%',
              maxWidth: '100%'
            }
          }}
        >
          <Box display='flex' alignItems='center' justifyContent='center'>
            <IconButton
              aria-label={texts.decrementQuantity}
              onClick={handleDecrement}
              size='small'
              sx={{
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '10px',
                marginRight: '4px',
                backgroundColor: '#f5f5f5',
                '&:hover': {
                  backgroundColor: '#e0e0e0'
                }
              }}
            >
              <Minus size={16} />
            </IconButton>
            <TextField
              size='small'
              type='number'
              inputProps={{
                min: 1,
                style: { textAlign: 'center', width: '40px' }
              }}
              value={quantity}
              onChange={(e) => {
                const val = parseInt(e.target.value)
                setQuantity(isNaN(val) || val < 1 ? 1 : val)
              }}
              sx={{
                mx: 1,
                '& input': {
                  padding: '8px',
                  textAlign: 'center'
                }
              }}
            />
            <IconButton
              aria-label={texts.incrementQuantity}
              onClick={handleIncrement}
              size='small'
              sx={{
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '10px',
                marginLeft: '4px',
                backgroundColor: '#f5f5f5',
                '&:hover': {
                  backgroundColor: '#e0e0e0'
                }
              }}
            >
              <Plus size={16} />
            </IconButton>
          </Box>
        </Grid>

        {/* Botón Añadir */}
        <Grid item xs={12} sm={12}>
          <Button
            variant='contained'
            color='primary'
            fullWidth
            onClick={handleAddToCart}
            sx={{
              height: '100%',
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none'
              },
              '&:active': {
                boxShadow: 'none'
              }
            }}
          >
            {texts.addToCart}
          </Button>
        </Grid>
      </Grid>
    </div>
  )
}

export default OrderingWidget
